// Enhanced authentication types and interfaces

export enum UserRole {
	USER = 0,
	ADMIN = 1,
	SUPERADMIN = 2,
}

export enum AccessLevel {
	BASIC = 0,
	ADVANCED = 1,
	SUPER = 2,
}

export interface UserClaims {
	role: UserRole;
	access: AccessLevel;
	permissions?: string[];
}

export interface AuthenticatedUser {
	displayName: string | null;
	email: string | null;
	uid: string | null;
	photoURL: string | null;
	claims: UserClaims;
	validated: boolean;
	emailVerified: boolean;
}

// Permission requirements for different admin sections
export interface PermissionRequirement {
	role: UserRole;
	access: AccessLevel;
	permissions?: string[];
}

// Role hierarchy - higher roles inherit permissions from lower roles
export const ROLE_HIERARCHY: Record<UserRole, number> = {
	[UserRole.USER]: 0,
	[UserRole.ADMIN]: 1,
	[UserRole.SUPERADMIN]: 2,
};

// Access level hierarchy - higher levels inherit permissions from lower levels
export const ACCESS_HIERARCHY: Record<AccessLevel, number> = {
	[AccessLevel.BASIC]: 0,
	[AccessLevel.ADVANCED]: 1,
	[AccessLevel.SUPER]: 2,
};

// Utility functions for converting between enum values and strings
export function userRoleToString(role: UserRole): string {
	switch (role) {
		case UserRole.USER:
			return "user";
		case UserRole.ADMIN:
			return "admin";
		case UserRole.SUPERADMIN:
			return "superadmin";
		default:
			return "user";
	}
}

export function stringToUserRole(role: string): UserRole {
	switch (role.toLowerCase()) {
		case "admin":
			return UserRole.ADMIN;
		case "superadmin":
			return UserRole.SUPERADMIN;
		case "user":
		default:
			return UserRole.USER;
	}
}

export function accessLevelToString(level: AccessLevel): string {
	switch (level) {
		case AccessLevel.BASIC:
			return "basic";
		case AccessLevel.ADVANCED:
			return "advanced";
		case AccessLevel.SUPER:
			return "super";
		default:
			return "basic";
	}
}

export function stringToAccessLevel(level: string): AccessLevel {
	switch (level.toLowerCase()) {
		case "advanced":
			return AccessLevel.ADVANCED;
		case "super":
			return AccessLevel.SUPER;
		case "basic":
		default:
			return AccessLevel.BASIC;
	}
}
